/**
 * Form Handlers Service
 * CRUD operations for form handler endpoints and their submission logs.
 */

import { supabase } from "./supabase";
import { isProduction } from "../utils/featureFlags";
import type {
  FormHandler,
  FormSubmission,
  CreateFormHandlerDTO,
  UpdateFormHandlerDTO,
  MergeFieldMapping,
} from "../types/formHandler.types";

export interface LookupMappingVerdict {
  templateField: string;
  ok: boolean;
  reason?:
    | "template_field_missing"
    | "lookup_id_missing"
    | "lookup_not_found"
    | "value_column_missing"
    | "default_row_missing"
    | "invalid_resolution_mode"
    | "unsupported_source_type";
}

/**
 * Save-time drift check. Calls the validate_lookup_mappings RPC, which
 * returns per-mapping verdicts against the current campaign_lookups
 * state. Mappings with non-lookup source types come back ok=true
 * without DB checks; structural validation is server-side in the edge
 * function's validateHandlerConfig.
 */
export async function validateLookupMappings(
  mappings: MergeFieldMapping[],
): Promise<LookupMappingVerdict[]> {
  if (!mappings || mappings.length === 0) {
    return [];
  }
  const { data, error } = await supabase.rpc("validate_lookup_mappings", {
    p_mappings: mappings,
  });
  if (error) {
    throw new Error(`validate_lookup_mappings failed: ${error.message}`);
  }
  return (data ?? []) as LookupMappingVerdict[];
}

const QA_URL = "https://qa-uslscrm.trysynapse.ai";
const PROD_URL = "https://uslscrm.trysynapse.ai";

export function getFormHandlerEndpointUrl(handlerId: string): string {
  // Single source of truth for env detection — correctly treats the prod
  // custom domain (crmapi.uslawshield.com) as production.
  const base = isProduction ? PROD_URL : QA_URL;
  return `${base}/form-handler/${handlerId}`;
}

export function getFormHandlerDirectUrl(handlerId: string): string {
  // Use the configured Supabase URL directly (the prod custom domain
  // crmapi.uslawshield.com on prod; the raw .supabase.co host on QA/dev).
  // Previously this regex-reconstructed `<ref>.supabase.co`, which produced a
  // broken `https://.supabase.co/...` URL under the custom domain.
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "";
  return `${supabaseUrl}/functions/v1/handle-form-submission?handler_id=${handlerId}`;
}

// =====================================================
// FORM HANDLERS CRUD
// =====================================================

export async function getFormHandlers(): Promise<FormHandler[]> {
  const { data, error } = await supabase
    .from("form_handlers")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) {
    throw error;
  }
  return (data || []) as FormHandler[];
}

export async function getFormHandler(id: string): Promise<FormHandler | null> {
  const { data, error } = await supabase
    .from("form_handlers")
    .select("*")
    .eq("id", id)
    .maybeSingle();

  if (error) {
    throw error;
  }
  return data as FormHandler | null;
}

export async function createFormHandler(
  input: CreateFormHandlerDTO,
  userId?: string,
): Promise<FormHandler> {
  const { data, error } = await supabase
    .from("form_handlers")
    .insert({ ...input, created_by: userId })
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as FormHandler;
}

export async function updateFormHandler(
  id: string,
  updates: UpdateFormHandlerDTO,
): Promise<FormHandler> {
  const { data, error } = await supabase
    .from("form_handlers")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as FormHandler;
}

export async function deleteFormHandler(id: string): Promise<void> {
  const { error } = await supabase.from("form_handlers").delete().eq("id", id);

  if (error) {
    throw error;
  }
}

export async function toggleFormHandlerActive(
  id: string,
  isActive: boolean,
): Promise<void> {
  const { error } = await supabase
    .from("form_handlers")
    .update({ is_active: isActive })
    .eq("id", id);

  if (error) {
    throw error;
  }
}

// =====================================================
// FORM SUBMISSIONS
// =====================================================

export async function getFormSubmissions(
  handlerId: string,
  page = 0,
  pageSize = 50,
): Promise<{ submissions: FormSubmission[]; total: number }> {
  const from = page * pageSize;
  const to = from + pageSize - 1;

  const { data, error, count } = await supabase
    .from("form_submissions")
    .select(`*, contacts(first_name, last_name, email)`, { count: "exact" })
    .eq("handler_id", handlerId)
    .order("submitted_at", { ascending: false })
    .range(from, to);

  if (error) {
    throw error;
  }
  return {
    submissions: (data || []) as FormSubmission[],
    total: count || 0,
  };
}
